import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PhotoFrame from '../components/PhotoFrame';
import AccessGate from '../components/AccessGate';
import { ArrowLeft, Shield, Award, CalendarRange, Users, Mail, BadgeCheck, Cake, Medal } from 'lucide-react';
import { isCurrentAppointment, appointmentLabel } from '../components/AppointmentsEditor';

const fmtBirthday = (b) => { if (!b) return ''; if (b.length === 10) { const d = new Date(b); return Number.isNaN(d.getTime()) ? b : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }); } const d = new Date(`2000-${b}`); return Number.isNaN(d.getTime()) ? b : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long' }); };

function Field({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 py-3 border-b" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
      <Icon size={18} className="mt-0.5" style={{ color: 'var(--navy)' }} />
      <div>
        <p className="text-xs uppercase tracking-wide" style={{ color: 'var(--olive)' }}>{label}</p>
        <p className="text-[15px]">{value || <span style={{ color: 'var(--olive)' }}>Not recorded</span>}</p>
      </div>
    </div>
  );
}

export default function MemberProfile() {
  const { memberId } = useParams();
  const { getMemberProfile, mediaUrl, classLabel, getMemberPhotos } = useAuth();
  const [m, setM] = useState(null);
  const [state, setState] = useState('loading');
  const [activePhoto, setActivePhoto] = useState(null);
  const [shared, setShared] = useState([]);

  useEffect(() => {
    getMemberProfile(memberId)
      .then((d) => { setM(d); setState('ok'); })
      .catch(() => setState('error'));
    getMemberPhotos(memberId).then(setShared).catch(() => setShared([]));
  }, [memberId, getMemberProfile, getMemberPhotos]);

  return (
    <AccessGate pageKey="members-directory">
      <div className="max-w-2xl mx-auto px-4 py-10 fade-in">
        <Link to="/members/directory" className="inline-flex items-center gap-1 text-sm mb-5" style={{ color: 'var(--navy)' }}>
          <ArrowLeft size={15} /> Back to Directory
        </Link>

        {state === 'loading' && <p className="text-sm" style={{ color: 'var(--olive)' }}>Loading…</p>}
        {state === 'error' && (
          <div className="bg-white rounded-lg p-8 shadow-sm border text-center" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
            <Users size={32} className="mx-auto mb-2" style={{ color: 'var(--olive)' }} />
            <p>This member profile could not be found.</p>
          </div>
        )}
        {state === 'ok' && m && (
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
            <div className="px-6 py-6 flex items-center gap-4" style={{ background: 'linear-gradient(180deg, var(--maroon), var(--maroon-dark))' }}>
              {(() => {
                const photos = m.photos || [];
                const primary = activePhoto || m.photo_url || (photos[0] && photos[0].url);
                const photo = primary ? (mediaUrl(primary) || primary) : m.picture;
                return photo ? (
                  <img
                    src={photo}
                    alt={m.name}
                    data-testid="member-profile-photo"
                    className="w-16 h-16 rounded-full object-cover"
                    style={{ border: '2px solid var(--gold)' }}
                  />
                ) : (
                  <div className="flex items-center justify-center w-16 h-16 rounded-full" data-testid="member-profile-crest" style={{ background: 'rgba(255,255,255,0.15)', border: '2px solid var(--gold)' }}>
                    <Shield size={30} color="var(--gold)" />
                  </div>
                );
              })()}
              <div>
                <h1 className="font-title text-2xl" style={{ color: 'var(--cream)' }}>{m.rank ? `${m.rank} ` : ''}{m.name}</h1>
                <span className="inline-block mt-1 px-2 py-0.5 rounded text-xs font-title" style={{ background: 'var(--gold)', color: '#3a2c06' }}>
                  {classLabel(m.member_class)}
                </span>
              </div>
            </div>
            {m.photos && m.photos.length > 1 && (
              <div className="px-6 pt-4 flex gap-2 flex-wrap" data-testid="member-profile-gallery">
                {m.photos.map((p) => (
                  <img
                    key={p.id}
                    src={mediaUrl(p.url) || p.url}
                    alt=""
                    onClick={() => setActivePhoto(p.url)}
                    className="w-14 h-14 object-cover rounded cursor-pointer transition-transform hover:scale-105"
                    style={{ border: (activePhoto || m.photo_url) === p.url ? '2px solid var(--gold)' : '2px solid rgba(0,0,0,0.12)' }}
                  />
                ))}
              </div>
            )}
            <div className="px-6 py-4">
              <Field icon={Award} label="Rank" value={m.rank} />
              <Field icon={Users} label="Unit" value={m.unit} />
              <Field icon={CalendarRange} label="Years Served" value={m.years_served} />
              {m.birthday && <Field icon={Cake} label="Birthday" value={fmtBirthday(m.birthday)} />}
              {(m.date_joined || m.joined_at) && <Field icon={CalendarRange} label="Member since" value={new Date(m.date_joined || m.joined_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })} />}
              {(m.appointments || []).length > 0 && (
                <div className="flex items-start gap-3 py-2" data-testid="member-profile-appointments">
                  <Medal size={16} className="mt-1 flex-shrink-0" style={{ color: 'var(--maroon)' }} />
                  <div className="text-sm">
                    <div className="text-xs font-title" style={{ color: 'var(--olive)' }}>Appointments</div>
                    {m.appointments.map((a) => <div key={a.id} style={{ color: 'var(--ink)' }}>{appointmentLabel(a)}{a.start_date ? ` · ${a.start_date.slice(0, 4)}` : ''}{a.end_date ? `–${a.end_date.slice(0, 4)}` : isCurrentAppointment(a) ? ' – present' : ''}</div>)}
                  </div>
                </div>
              )}
              {m.email && <Field icon={Mail} label="Email" value={m.email} />}
              {m.membership_expiry && (
                <Field icon={BadgeCheck} label="Membership" value={`Financial — valid to ${new Date(m.membership_expiry).toLocaleDateString()}`} />
              )}
              {m.bio && (
                <div className="pt-4" data-testid="member-profile-bio">
                  <p className="text-xs uppercase tracking-wide mb-1" style={{ color: 'var(--olive)' }}>Bio / Service Notes</p>
                  <p className="text-[15px] leading-relaxed" style={{ whiteSpace: 'pre-line' }}>{m.bio}</p>
                </div>
              )}
            </div>
          </div>
        )}
        {state === 'ok' && shared.some((p) => p.album) && (
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden mt-6 p-6" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid="member-albums">
            <h2 className="font-title text-lg mb-3" style={{ color: 'var(--navy)' }}>Albums contributed to</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {Object.entries(shared.reduce((acc, p) => { if (p.album) (acc[p.album] = acc[p.album] || []).push(p); return acc; }, {})).sort(([a], [b]) => a.localeCompare(b)).map(([name, ps]) => (
                <Link key={name} to={`/members/photos/album/${encodeURIComponent(name)}`} className="group block rounded overflow-hidden border bg-white" style={{ borderColor: 'rgba(0,0,0,0.1)' }} data-testid={`member-album-${name}`}>
                  <PhotoFrame src={mediaUrl(ps[0].url)} display={ps[0].display} alt={name} className="w-full h-24" style={{ background: 'rgba(0,0,0,0.04)' }} imgClassName="transition-transform duration-300 group-hover:scale-105" />
                  <div className="px-2 py-1.5"><div className="font-title text-xs truncate" style={{ color: 'var(--maroon)' }}>{name}</div><div className="text-[11px]" style={{ color: 'var(--olive)' }}>{ps.length} photo{ps.length !== 1 ? 's' : ''} by this member</div></div>
                </Link>
              ))}
            </div>
          </div>
        )}
        {state === 'ok' && shared.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden mt-6 p-6" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid="member-shared-photos">
            <h2 className="font-title text-lg mb-3" style={{ color: 'var(--navy)' }}>Shared Photos</h2>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {shared.map((p) => (
                <a key={p.id} href={mediaUrl(p.url)} target="_blank" rel="noreferrer" title={p.title || ''} data-testid={`shared-photo-${p.id}`}>
                  <img src={mediaUrl(p.url)} alt={p.title || ''} className="w-full h-24 object-cover rounded transition-transform hover:scale-105" style={{ border: '2px solid white' }} />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </AccessGate>
  );
}
